import React, { Component } from "react";
import styled from "styled-components";

import * as ApiErrors from "apis/errors";
import { ActionPermissionsObject, PermissionActionType } from "../../../../common/interfaces/index";
import { SlideCategories } from "../../../../common/SlideCategories/CategoryController";
import { Button } from "../../../Components/Button";
import { Dropdown } from "../../../Components/Dropdown";
import { Icon } from "../../../Components/Icon";
import { MenuItem } from "../../../Components/Menu";
import { ToggleSwitch } from "../../../Components/ToggleSwitch";
import { WithLabel } from "../../../Components/WithLabel";
import WorkspaceController from "../../../controllers/WorkspaceController";
import getLogger, { LogGroup } from "../../../core/logger";
import { Slide } from "../../../core/models/slide";
import { ContextFileTooLargeError } from "../../../core/services/aiGeneration";
import { aiContextFileTypes, validateFileTypeAndSize } from "../../../core/utilities/fileSourcesAndTypes";
import { PropertyPanelContainer, PropertySection } from "../../../EditorComponents/PropertyPanel";
import { ShowDialog, ShowErrorDialog } from "../../../react/components/Dialogs/BaseDialog";
import { FlexSpacer } from "../../../react/components/Gap";
import { FlexBox } from "../../../react/components/LayoutGrid";
import { SimpleLabel } from "../../../react/components/SimpleLabel";
import { themeColors } from "../../../react/sharedStyles";
import { PaneContainer } from "js/react/views/AIGeneration/components/PaneComponents";
import ImportFileDialog from "../../../react/views/ImportFile/ImportFileDialog";
import { _ } from "../../../vendor";
import PresentationEditorController from "../../PresentationEditor/PresentationEditorController";
import { AiIcon } from "../AiIcon";
import { AIImageSourcePicker, AiImageStylePopup } from "../GenerateImage/AiImageStyle";
import { AiSlideContentEditor } from "../GeneratePresentation/Components/AiSlideContentEditor";
import FileEntry from "../GeneratePresentation/Components/FileEntry";
import { LanguagePicker } from "../GeneratePresentation/Components/LanguagePicker";
import SlideAiController from "./SlideAiController";
import SlideAiIdeasList from "./SlideAiIdeasList";
import { isPPTAddin } from "../../../config";
import { withTranslation } from "react-i18next";

const logger = getLogger(LogGroup.AI);

const Pane = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    pointer-events: auto;
`;

const Container = styled.div`
    width: 100%;
    height: 100%;
    display: grid;
    grid-template-areas: 
"prompt options"
"preview preview";
    grid-template-rows: 400px 1fr;
    grid-template-columns: 1fr minmax(320px, 420px);
    background: #f9f9f9;
    row-gap: 10px;
`;

const Header = styled.div`
    height: 50px;
    background: #333;
    color: white;
    font-weight: bold;
    font-size: 18px;
    padding: 10px 10px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 10px;
`;

const PromptContainer = styled.div`
    grid-area: prompt;
    padding: 15px 20px 0px;
    display: grid;
    grid-template-rows: auto 1fr auto;
`;

const OptionsContainer = styled.div`
    grid-area: options;
    padding: 15px 20px 0px 0px;
    display: grid;
    grid-template-rows: auto 1fr;
`;

const PreviewContainer = styled.div`
    grid-area: preview;
    overflow: hidden;
    height: 100%;
`;

const TextContainer = styled.div`
    background: #fff;
    border: solid 1px #ccc;
    padding: 10px;
    width: 100%;
    pointer-events: auto;
    overflow-y: auto;
`;

class GenerateNewSlidePaneBase extends Component {
    constructor(props) {
        super(props);

        const presentationMetadata = props.presentation.get("metadata") || {};

        this.state = {
            currentSlide: null,
            isGenerating: false,
            isGenerated: false,
            slidePrompt: "",
            slideType: "auto",
            preserveExactText: false,
            usePresentationAsContext: true,
            language: presentationMetadata.language ?? "auto",
            webSearch: false,
            isDragging: false,
            isSavingFiles: false,
            imageSource: presentationMetadata.imageSource || "ai",
            imageStyle: presentationMetadata.imageStyle ?? "**theme**",
            imageModel: null,
        };

        const canGenerate = WorkspaceController.actionPermissions[ActionPermissionsObject.GENERATIVE_AI].use && WorkspaceController.actionPermissions[ActionPermissionsObject.GENERATIVE_AI_IMAGES].use;
        if (this.state.imageSource === "ai" && !canGenerate) {
            this.state.imageSource = "web";
        }

        this.promptRef = React.createRef();
        this.containerRef = React.createRef();
    }

    componentDidMount() {
        let { presentation } = this.props;

        let dummySlide = new Slide({
            id: "--new--",
            presentationId: presentation.id,
        }, {
            disconnected: true,
            autoLoad: false,
            presentation,
        });
        SlideAiController.setCurrentSlide(dummySlide);

        this.setState({ currentSlide: dummySlide });
    }

    handleGenerateSlide = async () => {
        let { slideType, language, preserveExactText, usePresentationAsContext, imageSource, imageStyle, imageModel, webSearch } = this.state;

        let slidePrompt = this.promptRef.current.getMarkdown();

        this.setState({ isGenerating: true, isGenerated: false });

        try {
            let props = {
                slideType,
                preserveExactText,
                usePresentationAsContext,
                language,
                webSearch,
                imageStyle,
                imageModel,
                imageSource,
                regenerateSlideImage: true,
            };

            await SlideAiController.regenerateIdeas(slidePrompt, props);
        } catch (err) {
            logger.error(err, "[GenerateNewSlidePane] handleGenerateSlide() error", { slidePrompt, slideType, preserveExactText, usePresentationAsContext, language, webSearch, imageStyle, imageModel, imageSource });

            const { t } = this.props;
            ShowErrorDialog({
                title: t("Error generating slide ideas"),
                message: t("Sorry, we couldn't generate slide ideas. Please try again."),
            });
            this.setState({ isGenerating: false, isGenerated: false });
            return;
        }

        if (this.props.slideCategories.length === 0) {
            this.setState({ isGenerating: false, isGenerated: false });
        } else {
            this.setState({ isGenerating: false, isGenerated: true });
        }
    }

    handleSelectIdea = idea => {
        const { onGeneratedSlide } = this.props;
        onGeneratedSlide(idea);
    }

    handleStartOver = () => {
        this.setState({ isGenerating: false, isGenerated: false });
    }

    handleDragOver = e => {
        e.preventDefault();
        e.stopPropagation();
        if (!this.state.isDragging) {
            this.setState({ isDragging: true });
        }
    }

    handleDragLeave = e => {
        e.preventDefault();
        e.stopPropagation();

        if (e.currentTarget === this.containerRef.current) {
            this.setState({ isDragging: false });
        }
    }

    handleDrop = async e => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ isDragging: false });

        let droppedFiles = Array.from(e.dataTransfer.files);

        for (let file of droppedFiles) {
            const errorMessage = validateFileTypeAndSize(file, aiContextFileTypes);
            if (errorMessage) {
                ShowErrorDialog({
                    title: this.props.t("File is not supported"),
                    message: errorMessage,
                });
                continue;
            }
            await this.processFile(file);
        }
    }

    handleAddFile = () => {
        ShowDialog(ImportFileDialog, {
            fileTypes: aiContextFileTypes,
            closeOnSuccess: true,
            onSuccess: async file => {
                if (file) {
                    this.processFile(file);
                }
            }
        });
    }

    processFile = async file => {
        try {
            await SlideAiController.parseDocument(file);
        } catch (err) {
            logger.error(err, "[GenerateNewSlidePane] processFile() error", { fileName: file.name, fileSize: file.size });

            if (err instanceof ApiErrors.ContentTooLargeError || err instanceof ContextFileTooLargeError) {
                ShowErrorDialog({
                    title: this.props.t("File is too large"),
                    message: err.message,
                });
                return;
            }

            ShowErrorDialog({
                title: this.props.t("Error processing {{fileName}}", { fileName: file.name }),
                message: this.props.t("Sorry, we couldn't process the file. Please try again with a different file."),
            });
        }
    }

    handleRemoveFile = file => {
        SlideAiController.removeFile(file.name);
    }

    render() {
        let { currentTheme, files, forTeamSlide, t } = this.props;
        let { slidePrompt, imageSource, imageStyle, preserveExactText, usePresentationAsContext, slideType, language, isGenerating, isGenerated, webSearch, isDragging } = this.state;

        if (WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.GENERATIVE_AI, PermissionActionType.USE)) {
            return <PaneContainer cta="PresentationBot" />;
        }

        if (isGenerating || isGenerated) {
            return (
                <Pane>
                    {isGenerated &&
                        <Header>
                            <Button unframed textColor="white" hoverColor="#555" onClick={this.handleStartOver}>
                                <Icon>replay</Icon>
                                {t("Start Over")}
                            </Button>
                        </Header>
                    }
                    <PreviewContainer>
                        <SlideAiIdeasList onSelect={this.handleSelectIdea} />
                    </PreviewContainer>
                </Pane>
            );
        }

        return (
            <Pane>
                <Container onDragOver={this.handleDragOver} onDragLeave={this.handleDragLeave} onDrop={this.handleDrop} ref={this.containerRef}>
                    <PromptContainer>
                        <FlexBox left middle>
                            <SimpleLabel>{t("SLIDE PROMPT")}</SimpleLabel>
                            <FlexSpacer />
                        </FlexBox>
                        <TextContainer>
                            <AiSlideContentEditor ref={this.promptRef} text={slidePrompt} onChange={slidePrompt => this.setState({ slidePrompt })} t={t} />
                        </TextContainer>
                        <FlexBox left middle gap={20}>
                            <WithLabel right tight label={t("Enable Web Search")}>
                                <ToggleSwitch value={webSearch} onChange={webSearch => this.setState({ webSearch })} />
                            </WithLabel>
                            <Button unframed onClick={this.handleAddFile}>
                                <Icon>{isDragging ? "check_circle" : "add_circle"}</Icon>
                                {isDragging ? t("Drop files here") : t("Add file...")}
                            </Button>
                        </FlexBox>
                        {files.length > 0 && (
                            <FlexBox wrap gap={10} horizontalAlign="start" fillWidth>
                                {files.map((file, index) => (
                                    <FileEntry key={index} file={file} onRemove={this.handleRemoveFile} />
                                ))}
                            </FlexBox>
                        )}
                    </PromptContainer>
                    <OptionsContainer>
                        <FlexBox left middle>
                            <SimpleLabel>{t("OPTIONS")}</SimpleLabel>
                        </FlexBox>
                        <PropertyPanelContainer fullHeight style={{ border: "solid 1px #ccc" }}>
                            <PropertySection>
                                <WithLabel label={t("Slide Type")}>
                                    <Dropdown value={slideType} onChange={slideType => this.setState({ slideType })}>
                                        <MenuItem value="auto">{t("Auto Detect")}</MenuItem>
                                        {Object.keys(SlideCategories).map(key => (
                                            <MenuItem key={key} value={SlideCategories[key]}>{_.startCase(_.toLower(SlideCategories[key]))}</MenuItem>
                                        ))}
                                    </Dropdown>
                                </WithLabel>
                                <WithLabel label={t("Language")}>
                                    <LanguagePicker value={language} onChange={language => this.setState({ language })} />
                                </WithLabel>
                            </PropertySection>
                            {!forTeamSlide && !isPPTAddin &&
                                <PropertySection>
                                    <WithLabel
                                        left
                                        label={t("Use Presentation Context")}
                                        toolTip={t("Generate this slide using the context of the entire presentation.")}
                                    >
                                        <ToggleSwitch value={usePresentationAsContext} onChange={usePresentationAsContext => this.setState({ usePresentationAsContext })} />
                                    </WithLabel>
                                </PropertySection>
                            }
                            <PropertySection>
                                <WithLabel
                                    left
                                    label={t("Preserve Exact Text")}
                                    toolTip={t("Try to keep the text in the generated slides exactly as it appears in the prompt.")}
                                >
                                    <ToggleSwitch value={preserveExactText} onChange={preserveExactText => this.setState({ preserveExactText })} />
                                </WithLabel>
                            </PropertySection>
                            <PropertySection>
                                <WithLabel label={t("Image Source")}>
                                    <AIImageSourcePicker value={imageSource} onChange={imageSource => this.setState({ imageSource })} />
                                </WithLabel>
                                {imageSource == "ai" && (
                                    <WithLabel label={t("Ai Image Style")}>
                                        <AiImageStylePopup theme={currentTheme} imageStyle={imageStyle} onChange={(imageStyle, imageModel) => this.setState({ imageStyle, imageModel })} />
                                    </WithLabel>
                                )}
                            </PropertySection>
                            <FlexSpacer />
                            <PropertySection fullWidth color={themeColors.lightBlue}>
                                <FlexBox fill>
                                    <Button
                                        large
                                        color={themeColors.aiColor}
                                        disabled={isGenerating}
                                        onClick={this.handleGenerateSlide}
                                        height={48}
                                        style={{ padding: "0 10px", margin: "10px 10px", width: "100%" }}
                                        showSpinner={isGenerating}
                                    >
                                        <AiIcon />
                                        {t("Generate Slide")}
                                    </Button>
                                </FlexBox>
                            </PropertySection>
                        </PropertyPanelContainer>
                    </OptionsContainer>

                </Container>
            </Pane>
        );
    }
}

export default withTranslation()(PresentationEditorController.withState(SlideAiController.withState(GenerateNewSlidePaneBase)));
